/* ============================================================
   SETTINGS — site-wide config the owner controls:
   - the enquiry recipient email (where briefs are sent; the Cloud
     Function reads this, falling back to its deploy default when blank)
   - social links (Instagram / X / Facebook); each shows in the footer
     only when a URL is set.
   Stored in settings/global (public read, admin write).
   ============================================================ */
import { useEffect, useState } from 'react'
import { useSettings } from '../content/ContentContext.jsx'
import { saveSettings } from '../content/loader.js'
import { useAuth } from './AuthContext.jsx'
import { Icon } from '../components/Icon.jsx'

const MIN_PW = 8

function pwError(err) {
  const code = (err && err.code) || ''
  if (code.includes('wrong-password') || code.includes('invalid-credential'))
    return 'Your current password is incorrect.'
  if (code.includes('weak-password')) return `New password must be at least ${MIN_PW} characters.`
  if (code.includes('requires-recent-login')) return 'Please sign out and back in, then try again.'
  if (code.includes('too-many-requests')) return 'Too many attempts. Try again later.'
  return 'Could not change the password. Please try again.'
}

/* Change-password form. Re-authenticates with the current password (handled in
   lib/auth.changePassword), so it works regardless of session age. */
function PasswordSection() {
  const { changePassword } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState(null)

  const tooShort = next.length > 0 && next.length < MIN_PW
  const mismatch = confirm.length > 0 && next !== confirm
  const canSubmit = current && next.length >= MIN_PW && next === confirm && !busy

  async function submit() {
    if (!canSubmit) return
    setBusy(true)
    setErr(null)
    setDone(false)
    try {
      await changePassword(current, next)
      setDone(true)
      setCurrent('')
      setNext('')
      setConfirm('')
      setTimeout(() => setDone(false), 3000)
    } catch (e) {
      setErr(pwError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fgroup">
      <div className="fgroup-h">Password</div>
      <div className="ff">
        <label>Current password</label>
        <input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </div>
      <div className="ff">
        <label>New password</label>
        <input
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        {tooShort && <div className="ff-err">At least {MIN_PW} characters.</div>}
      </div>
      <div className="ff">
        <label>Confirm new password</label>
        <input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {mismatch && <div className="ff-err">Passwords don’t match.</div>}
      </div>
      {err && <div className="ff-err">{err}</div>}
      <div className="sections-actions">
        {done && <span className="saved-note">Password changed ✓</span>}
        <button className="btn-pub" onClick={submit} disabled={!canSubmit}>
          {busy ? 'Saving…' : 'Change password'}
        </button>
      </div>
    </div>
  )
}

export function SettingsScreen() {
  const live = useSettings()
  const [enquiryEmail, setEnquiryEmail] = useState('')
  const [social, setSocial] = useState({ instagram: '', x: '', facebook: '' })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Hydrate the form from the live settings doc.
  useEffect(() => {
    setEnquiryEmail(live.enquiryEmail || '')
    setSocial({
      instagram: live.social?.instagram || '',
      x: live.social?.x || '',
      facebook: live.social?.facebook || '',
    })
  }, [live])

  const emailValid = !enquiryEmail.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enquiryEmail.trim())

  async function save() {
    if (!emailValid) return
    setSaving(true)
    setSaved(false)
    try {
      await saveSettings({
        enquiryEmail: enquiryEmail.trim(),
        social: {
          instagram: social.instagram.trim(),
          x: social.x.trim(),
          facebook: social.facebook.trim(),
        },
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      console.warn('[settings] save failed.', e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="settings">
      <div className="fgroup">
        <div className="fgroup-h">Enquiries</div>
        <div className="ff">
          <label>Recipient email</label>
          <input
            type="email"
            value={enquiryEmail}
            placeholder="where confidential briefs are sent"
            onChange={(e) => setEnquiryEmail(e.target.value)}
          />
          {!emailValid && <div className="ff-err">That email address looks invalid.</div>}
          <div className="ff-hint">Leave blank to use the system default.</div>
        </div>
      </div>

      <div className="fgroup">
        <div className="fgroup-h">Social links</div>
        <div className="ff-hint" style={{ marginBottom: 14 }}>
          Each icon appears in the site footer only when a link is set.
        </div>
        {[
          { key: 'instagram', label: 'Instagram', icon: 'instagram' },
          { key: 'x', label: 'X (Twitter)', icon: 'x' },
          { key: 'facebook', label: 'Facebook', icon: 'facebook' },
        ].map((s) => (
          <div className="ff social-ff" key={s.key}>
            <span className="social-ic">
              <Icon name={s.icon} size={18} />
            </span>
            <div style={{ flex: 1 }}>
              <label>{s.label}</label>
              <input
                type="url"
                value={social[s.key]}
                placeholder="https://…"
                onChange={(e) => setSocial((v) => ({ ...v, [s.key]: e.target.value }))}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="sections-actions">
        {saved && <span className="saved-note">Saved ✓</span>}
        <button className="btn-pub" onClick={save} disabled={saving || !emailValid}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      <PasswordSection />
    </div>
  )
}
